import { useCallback } from 'react';
import { useFile, useSelection, useVisualizer, useUI } from '../context/AppContext.jsx';
import { convertWaypointsToWorldCoords, featureToWorld, worldToFeature } from '../utils/coordinateTransforms.js';
import { getWaypointIndicesToMirror } from '../services/MirrorService.js';

export function useMassEdit() {
  const {
    parser,
    currentWaypoints,
    setCurrentWaypoints,
    nodes,
    currentFeatures,
  } = useFile();
  const { multiSelectedIndices } = useSelection();
  const { visualizer } = useVisualizer();
  const { showStatus, setIsLoading, setLoadingProgress, setLoadingText } = useUI();

  const applyMassEdit = useCallback(async (offsets, scope, nodeIndex = null) => {
    try {
      // Convert offsets from mm/degrees to meters/radians
      // These offsets are in WORLD coordinates
      const offsetX = offsets.x / 1000;
      const offsetY = offsets.y / 1000;
      const offsetZ = offsets.z / 1000;
      const offsetRX = offsets.rx * Math.PI / 180;
      const offsetRY = offsets.ry * Math.PI / 180;
      const offsetRZ = offsets.rz * Math.PI / 180;

      // Use service to determine which waypoints to update
      const { indices, error } = getWaypointIndicesToMirror(
        scope,
        currentWaypoints,
        multiSelectedIndices,
        nodes,
        nodeIndex
      );
      
      if (error) {
        showStatus(error, 'error');
        return;
      }

      const showProgress = indices.length > 50;
      if (showProgress) {
        setIsLoading(true);
        setLoadingText('Applying offsets...');
        setLoadingProgress(0);
      }

      let updatedCount = 0;
      const totalItems = indices.length;
      const updatedWaypoints = [...currentWaypoints];

      for (let i = 0; i < indices.length; i++) {
        const index = indices[i];
        const waypoint = currentWaypoints[index];
        
        // Find the feature for this waypoint
        const feature = currentFeatures.find(f => f.name === waypoint.feature);
        
        // Convert waypoint from feature-local to world coordinates
        let worldCoords;
        if (feature) {
          worldCoords = featureToWorld(waypoint.position, waypoint.rotation, feature);
        } else {
          // No feature found, treat as already in world coords
          worldCoords = {
            position: waypoint.position,
            rotation: waypoint.rotation
          };
        }
        
        // Apply offset in world coordinates
        const newWorldPosition = {
          x: worldCoords.position.x + offsetX,
          y: worldCoords.position.y + offsetY,
          z: worldCoords.position.z + offsetZ
        };

        const newWorldRotation = {
          rx: worldCoords.rotation.rx + offsetRX,
          ry: worldCoords.rotation.ry + offsetRY,
          rz: worldCoords.rotation.rz + offsetRZ
        };
        
        // Convert back to feature-local coordinates for storage
        let newFeatureLocal;
        if (feature) {
          newFeatureLocal = worldToFeature(newWorldPosition, newWorldRotation, feature);
        } else {
          // No feature, use world coords directly
          newFeatureLocal = {
            position: newWorldPosition,
            rotation: newWorldRotation
          };
        }

        // Update parser with feature-local coordinates
        parser.updateWaypointPosition(index, newFeatureLocal.position);
        parser.updateWaypointRotation(index, newFeatureLocal.rotation);

        // Update local data with feature-local coordinates
        updatedWaypoints[index] = {
          ...updatedWaypoints[index],
          position: newFeatureLocal.position,
          rotation: newFeatureLocal.rotation,
        };

        // Also update via-point if it exists
        if (waypoint.viaPoint) {
          // Convert via-point to world coordinates
          let viaWorldCoords;
          if (feature) {
            viaWorldCoords = featureToWorld(waypoint.viaPoint.position, waypoint.viaPoint.rotation, feature);
          } else {
            viaWorldCoords = {
              position: waypoint.viaPoint.position,
              rotation: waypoint.viaPoint.rotation
            };
          }
          
          // Apply offset in world coordinates
          const newViaWorldPosition = {
            x: viaWorldCoords.position.x + offsetX,
            y: viaWorldCoords.position.y + offsetY,
            z: viaWorldCoords.position.z + offsetZ
          };

          const newViaWorldRotation = {
            rx: viaWorldCoords.rotation.rx + offsetRX,
            ry: viaWorldCoords.rotation.ry + offsetRY,
            rz: viaWorldCoords.rotation.rz + offsetRZ
          };
          
          // Convert back to feature-local coordinates
          let newViaFeatureLocal;
          if (feature) {
            newViaFeatureLocal = worldToFeature(newViaWorldPosition, newViaWorldRotation, feature);
          } else {
            newViaFeatureLocal = {
              position: newViaWorldPosition,
              rotation: newViaWorldRotation
            };
          }

          parser.updateViaPointPosition(index, newViaFeatureLocal.position);
          parser.updateViaPointRotation(index, newViaFeatureLocal.rotation);

          updatedWaypoints[index].viaPoint = {
            ...updatedWaypoints[index].viaPoint,
            position: newViaFeatureLocal.position,
            rotation: newViaFeatureLocal.rotation,
          };
        }

        updatedCount++;

        // Update progress for large operations
        if (showProgress && i % 10 === 0) {
          const progress = ((i + 1) / totalItems) * 100;
          setLoadingProgress(progress);
          setLoadingText(`Updated ${i + 1} of ${totalItems} waypoints`);
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }

      // Update all waypoints at once
      setCurrentWaypoints(updatedWaypoints);

      // Hide progress overlay
      if (showProgress) {
        setIsLoading(false);
      }

      // Re-visualize all waypoints
      if (visualizer) {
        const waypointsForVis = convertWaypointsToWorldCoords(updatedWaypoints, currentFeatures);
        visualizer.visualizeWaypoints(waypointsForVis);
      }

      showStatus(`Updated ${updatedCount} waypoint(s)`, 'success');
    } catch (error) {
      setIsLoading(false);
      console.error('Mass edit error:', error);
      showStatus(`Error: ${error.message}`, 'error');
    }
  }, [parser, visualizer, currentWaypoints, setCurrentWaypoints, multiSelectedIndices, nodes, currentFeatures, showStatus, setIsLoading, setLoadingProgress, setLoadingText]);

  return { applyMassEdit };
}

